import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { api, ApiError } from '@/lib/api'
import {
  hasStaffAccess,
  is2FAPending,
  loginWithPassword,
  verifyLogin2FA,
} from '@/lib/auth-api'
import { SESSION_KEY, USER_KEY } from '@/lib/config'
import type { StaffMeResponse, StaffPermissions, StaffInfo } from '@/types'

interface AuthContextValue {
  token: string | null
  staff: StaffInfo | null
  permissions: StaffPermissions | null
  loading: boolean
  error: string | null
  isStaff: boolean
  login: (email: string, password: string) => Promise<{ status: 'ok' } | { status: '2fa'; tempToken: string }>
  verify2FA: (tempToken: string, code: string) => Promise<void>
  logout: () => void
  setToken: (token: string) => void
  completeSession: (sessionToken: string, user?: unknown) => Promise<boolean>
}

const AuthContext = createContext<AuthContextValue | null>(null)

function persistSession(sessionToken: string, user?: unknown) {
  localStorage.setItem(SESSION_KEY, sessionToken)
  if (user) localStorage.setItem(USER_KEY, JSON.stringify(user))
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const [token, setTokenState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(SESSION_KEY)
    } catch {
      return null
    }
  })
  const [staff, setStaff] = useState<StaffInfo | null>(null)
  const [permissions, setPermissions] = useState<StaffPermissions | null>(null)
  const [loading, setLoading] = useState(!!token)
  const [error, setError] = useState<string | null>(null)

  const fetchStaffSession = useCallback(async (sessionToken: string) => {
    const prev = localStorage.getItem(SESSION_KEY)
    if (prev !== sessionToken) localStorage.setItem(SESSION_KEY, sessionToken)
    const data = await api<StaffMeResponse>('/api/ts/me')
    setStaff(data.staff)
    setPermissions(data.permissions)
    return hasStaffAccess(data.permissions)
  }, [])

  const completeSession = useCallback(
    async (sessionToken: string, user?: unknown) => {
      persistSession(sessionToken, user)
      setTokenState(sessionToken)
      setLoading(true)
      setError(null)
      try {
        return await fetchStaffSession(sessionToken)
      } finally {
        setLoading(false)
      }
    },
    [fetchStaffSession],
  )

  const setToken = useCallback(
    (t: string) => {
      void completeSession(t)
    },
    [completeSession],
  )

  const logout = useCallback(() => {
    try {
      localStorage.removeItem(SESSION_KEY)
      localStorage.removeItem(USER_KEY)
    } catch {
      /* ignore */
    }
    setTokenState(null)
    setStaff(null)
    setPermissions(null)
    setLoading(false)
    navigate('/login')
  }, [navigate])

  useEffect(() => {
    if (!token) {
      setStaff(null)
      setPermissions(null)
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)
    setError(null)

    fetchStaffSession(token)
      .catch((err: unknown) => {
        if (cancelled) return
        if (err instanceof ApiError && err.status === 401) {
          try {
            localStorage.removeItem(SESSION_KEY)
            localStorage.removeItem(USER_KEY)
          } catch {
            /* ignore */
          }
          setTokenState(null)
          setStaff(null)
          setPermissions(null)
          return
        }
        setError(err instanceof Error ? err.message : 'Failed to verify session')
        setStaff(null)
        setPermissions(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [token, fetchStaffSession])

  useEffect(() => {
    const onLogout = () => logout()
    const onAuthRequired = () => logout()
    window.addEventListener('clyr-admin-logout', onLogout)
    window.addEventListener('clyr-admin-auth-required', onAuthRequired)
    return () => {
      window.removeEventListener('clyr-admin-logout', onLogout)
      window.removeEventListener('clyr-admin-auth-required', onAuthRequired)
    }
  }, [logout])

  const login = useCallback(async (email: string, password: string) => {
    const result = await loginWithPassword(email, password)
    if (is2FAPending(result)) {
      return { status: '2fa' as const, tempToken: result.tempToken }
    }
    await completeSession(result.sessionToken, result.user)
    return { status: 'ok' as const }
  }, [completeSession])

  const verify2FA = useCallback(
    async (tempToken: string, code: string) => {
      const result = await verifyLogin2FA(tempToken, code)
      await completeSession(result.sessionToken, result.user)
    },
    [completeSession],
  )

  const isStaff = useMemo(() => hasStaffAccess(permissions), [permissions])

  const value = useMemo(
    () => ({
      token,
      staff,
      permissions,
      loading,
      error,
      isStaff,
      login,
      verify2FA,
      logout,
      setToken,
      completeSession,
    }),
    [token, staff, permissions, loading, error, isStaff, login, verify2FA, logout, setToken, completeSession],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
